<template>
  <section id="shareForm">
    <form @submit.prevent="submitForm">
      <div class="form-group">
        <div class="form-row">
          <input required type="text" :placeholder="shareFormNameLabel" :disabled="sent" v-model="fullName" name="fullName" id="fullName" :aria-label="shareFormNameLabel" />
        </div>

        <div class="form-row">
          <input required type="email" :placeholder="shareFormEmailLabel" :disabled="sent" v-model="email" name="email" id="email" :aria-label="shareFormEmailLabel" />
        </div>

        <div class="form-row">
          <input type="text" :placeholder="shareFormPostcodeLabel" :disabled="sent" v-model="postcode" name="postcode" id="postcode" :aria-label="shareFormPostcodeLabel" />
        </div>
      </div>

      <div class="form-group small">
        <div class="form-row">
          <input type="checkbox" v-model="confirmTCs" :disabled="sent" required name="confirmTCs" id="confirmTCs"/>
          <label for="confirmTCs">
            {{ shareFormTermsLabelStart }} <a @click.prevent="showTermsNotice">{{ siteName }} {{ shareFormTermsLabelEnd }}</a>
          </label>
        </div>
        <div class="form-row">
          <input type="checkbox" v-model="ageCheck" :disabled="sent" required name="ageCheck" id="ageCheck" />
          <label for="ageCheck">
            {{ shareFormConsentLabel }}
          </label>
        </div>
        <div class="form-row">
          <input type="checkbox" :disabled="sent" v-model="consentKaleiderUpdates" name="consentKaleiderUpdates" id="consentKaleiderUpdates" />
          <label for="consentKaleiderUpdates">
              {{ shareFormSubscribeLabel }} <a @click.prevent="showPrivacyNotice">({{ shareFormPrivacyLinkText }})</a>
          </label>
        </div>
      </div>

      <div class="submit-row">
        <p class="cta-container">
          <SwitchButton :text="shareFormSendButton" :activeText="shareFormSendButtonDisabled" :active="sent || sending" :disabled="sent || sending" type="submit" :aria-label="shareFormSendButton" />
        </p>

        <div v-if="error" class="error">
          <h2>{{ shareFormErrorHeading }}</h2>
          <p v-if="errorCode === 'client'">
            {{ shareFormTimeoutError }}
          </p>
          <p v-else>
            {{ shareFormServerError }}
          </p>
        </div>
      </div>
    </form>

    <FormNotice ref="termsNotice" :modalCloseButton="modalCloseButton">
      <slot name="termsSlot"></slot>
    </FormNotice>

    <FormNotice ref="privacyNotice" :modalCloseButton="modalCloseButton">
      <slot name="privacySlot"></slot>
    </FormNotice>

  </section>
</template>

<script>
import axios from 'axios'
import SwitchButton from "@/components/SwitchButton.vue";
import FormNotice from "@/components/FormNotice.vue";

export default {
  name: "shareForm",
  components: {
    SwitchButton,
    FormNotice,
  },
  data() {
    return {
      fullName: "",
      email: "",
      postcode: "",
      confirmTCs: false,
      ageCheck: false,
      consentKaleiderUpdates: false,
      sending: false,
      sent: false,
      error: false,
      errorCode: "",
    }
  },
  props: [
    "site",
    "siteName",
    "shareFormNameLabel",
    "shareFormEmailLabel",
    "shareFormPostcodeLabel",
    "shareFormTermsLabelStart",
    "shareFormTermsLabelEnd",
    "shareFormConsentLabel",
    "shareFormSubscribeLabel",
    "shareFormPrivacyLinkText",
    "shareFormSendButton",
    "shareFormSendButtonDisabled",
    "shareFormErrorHeading",
    "shareFormTimeoutError",
    "shareFormServerError",
    "modalCloseButton"
  ],
  methods: {
    async submitForm()  {
      if (this.sending || this.sent) {
        return
      }
      this.sending = true

      const data = new FormData()

      data.append("site", this.site)

      data.append("full_name", this.fullName)
      data.append("email", this.email)
      data.append("postcode", this.postcode)
      data.append("agree_terms", this.confirmTCs)
      data.append("age_check", this.ageCheck)
      data.append("consent_kaleider_updates", this.consentKaleiderUpdates)

      data.append('panel_settings', localStorage.getItem('panel'))
      const svgBlob = new Blob([localStorage.getItem("svg")], {type: "image/svg+xml"})
      data.append("svg", svgBlob, "submission.svg")

      try {
        await axios.post(
          `/api/submission/`,
          data,
          {
            headers: {
            'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
            },
            timeout: 5000
          },
        )

        this.sent = true;
        this.error = false
        this.errorCode = null
        this.$emit("sent")

      } catch (err) {
        console.log(err)
        this.error = true
        this.sending = false

        if (!err.response) {
          // never hit the server... so it's a client issue
          this.errorCode = "client"
        } else {
          this.errorCode = err.response.status
        }
      }
    },
    showTermsNotice () {
      this.$refs.termsNotice.show()
    },
    showPrivacyNotice () {
      this.$refs.privacyNotice.show()
    },
  }
}
</script>

<style lang="scss" scoped>
  .form-group {
    padding: 0.5em 0;

    &.small {
      font-size: 15px;
    }
  }

  .form-row {
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    margin: 1em 0;

    &:first-child {
      margin-top: 0;
    }

    input[type=text], input[type=email] {
      width: 100%;
      padding: 0.5em 0.1em;
      font-size: 20px;
      border:none;
      border-bottom: solid 1px $color-black;
      box-shadow: none;
      outline: none;

      &:disabled {
        color: $color-grey-black;
        background: transparent;
      }

      &:invalid {
        border:none;
        -moz-ui-invalid: none;
        box-shadow: none;
        border-bottom: solid 1px $color-red;
      }
    }

    input[type=checkbox] {
      opacity: 0;

      &:disabled {
        &, + label, + label a {
          color: $color-grey-black;
          background: transparent;
        }
        + label a {
          cursor: pointer;
        }
        &, + label {
          cursor: default;
        }
      }

      + label {
        cursor: pointer;
        padding-left: 0.5em;

        &:before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          display: block;
          height: calc(1em - 4px);
          width: calc(1em - 4px);
          padding: 3px;
          border-radius: 50%;
          border: solid 2px $color-red;
          background-color: $color-white;
          transition: background-color ease-in-out 0.2s;
        }

        a {
          text-decoration: underline;
        }
      }

      &:checked + label:before {
        background-clip: content-box;
        background-color: $color-red;
      }

    }

  }
</style>
